import { supabase } from './supabase';
import { CreditAnalyticsService } from './credit-analytics';
import { EmailService } from './email-service';
import { broadcastCreditChange } from './credit-events';
import * as Sentry from '@sentry/react';

export interface CreditTransaction {
  id: string;
  userId: string;
  amount: number;
  type: 'deduction' | 'addition' | 'purchase' | 'subscription_renewal' | 'referral_bonus' | 'refund';
  reason: string;
  metadata?: {
    analysisId?: string;
    subscriptionId?: string;
    paymentIntentId?: string;
    platform?: string;
    enhancementId?: string;
    originalCreativeId?: string;
    rewardId?: string;
    rewardType?: string;
  };
  createdAt: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  monthlyCredits: number;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  isPopular?: boolean;
  stripePriceIdMonthly?: string | null;
  stripePriceIdYearly?: string | null;
}

export interface CreditUsage {
  totalUsed: number;
  totalAvailable: number;
  currentMonth: {
    used: number;
    available: number;
  };
  breakdown: {
    platform: string;
    count: number;
    credits: number;
  }[];
}

export class CreditService {
  // Simplified pricing: 1 credit = 1 comprehensive analysis across ALL platforms
  private static readonly ANALYSIS_COST = 1;

  /**
   * Update the users.credits field to show current total available credits
   * This keeps the database table synchronized for easy viewing
   */
  private static async updateUsersCreditDisplay(userId: string): Promise<void> {
    try {
      // Skip updating display field when called from API context to avoid authentication issues
      // The display field will be updated when the user next loads the frontend
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('Skipping users.credits display update - no authenticated context');
        return;
      }

      // Get current credit balance using the proper calculation method
      const currentBalance = await this.getCreditBalance();
      
      // Update the display field with the correct calculated balance
      const { error } = await supabase
        .from('users')
        .update({ credits: currentBalance })
        .eq('id', userId);
        
      if (error) {
        console.error('Failed to update users.credits display field:', error);
      } else {
        console.log(`✅ Updated users.credits display field: ${currentBalance} credits`);
      }
    } catch (error) {
      console.error('Error in updateUsersCreditDisplay:', error);
      // Don't capture this error in Sentry when called from API context - it's expected
      if (error.message && !error.message.includes('User not authenticated')) {
        Sentry.captureException(error, {
          tags: {
            operation: 'updateUsersCreditDisplay',
            component: 'CreditService',
          },
          extra: {
            userId,
          },
        });
      }
    }
  }

  private static readonly SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
    {
      id: 'trial',
      name: 'Free Trial',
      monthlyCredits: 3,
      priceMonthly: 0,
      priceYearly: 0,
      features: [
        '3 comprehensive analyses per month',
        'All platform compliance checking',
        'Basic support',
        '1 project',
        '1 GB storage',
        'Credits reset monthly'
      ]
    },
    {
      id: 'starter',
      name: 'Starter',
      monthlyCredits: 25,
      priceMonthly: 1590,
      priceYearly: 191,
      features: [
        '25 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'Priority support',
        '3 projects',
        '15 GB storage',
        'Extra credits: $14.90 per 20 credits'
      ],
      stripePriceIdMonthly: 'price_1P3gYyRJs5nJ1g2fgL9s4bN7' // Starter Plan Monthly
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyCredits: 100,
      priceMonthly: 3990,
      priceYearly: 479,
      isPopular: true,
      features: [
        '100 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'Priority support',
        'Unlimited projects',
        'Team collaboration (up to 3 members)',
        '50 GB storage',
        'Detailed analytics',
        'Export reports',
        'Extra credits: $25.90 per 50 credits'
      ],
      stripePriceIdMonthly: 'price_1P3gZzRJs5nJ1g2fI2Z5y3cE' // Pro Plan Monthly
    },
    {
      id: 'agency',
      name: 'Agency',
      monthlyCredits: 350,
      priceMonthly: 9990,
      priceYearly: 1199,
      features: [
        '350 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'White-label reports',
        'API access',
        'Team collaboration (up to 10 members)',
        '200 GB storage',
        'Priority analysis queue',
        'Dedicated account manager',
        'Custom integrations',
        'Extra credits: $60.90 per 150 credits'
      ],
      stripePriceIdMonthly: 'price_1P3gbGRJs5nJ1g2fB7V2k3fA' // Agency Plan Monthly
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthlyCredits: 1000,
      priceMonthly: 29990,
      priceYearly: 3599,
      features: [
        '1,000 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'Enterprise-grade security',
        'Custom deployment options',
        'Unlimited team members',
        '1 TB storage',
        'Priority analysis queue',
        'Dedicated account manager',
        'Custom integrations',
        'SLA guarantee',
        'Extra credits: $150.90 per 500 credits'
      ],
      stripePriceIdMonthly: 'price_1P3gcCRJs5nJ1g2fH5g4j6kL' // Enterprise Plan Monthly
    }
  ];

  /**
   * Get separate plan and purchased credit counts for UI display
   */
  static async getSeparateCreditCounts(): Promise<{
    planCredits: number;
    purchasedCredits: number;
    totalCredits: number;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get subscription data
      const { data: subscriptionData } = await supabase
        .from('user_subscriptions')
        .select(`
          subscription_plans!user_subscriptions_plan_id_fkey (
            monthly_credits
          )
        `)
        .eq('user_id', user.id)
        .in('status', ['active', 'canceled'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const monthlyCredits = subscriptionData?.subscription_plans?.monthly_credits || 3;

      // Get usage and purchased credits
      const { data: limitData } = await supabase
        .from('subscription_limits')
        .select('current_analyses_used, purchased_analysis_credits')
        .eq('user_id', user.id)
        .maybeSingle();

      const usedCredits = limitData?.current_analyses_used || 0;
      const purchasedCredits = limitData?.purchased_analysis_credits || 0;
      
      const planCredits = Math.max(0, monthlyCredits - usedCredits);
      const totalCredits = planCredits + purchasedCredits;

      return {
        planCredits,
        purchasedCredits,
        totalCredits
      };
    } catch (error) {
      console.error('Get separate credit counts error:', error);
      return {
        planCredits: 0,
        purchasedCredits: 0,
        totalCredits: 0
      };
    }
  }

  /**
   * Get current user's credit balance - includes both plan and purchased credits
   */
  static async getCreditBalance(): Promise<number> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get subscription data with usage information
      const { data: subscriptionData, error: subError } = await supabase
        .from('user_subscriptions')
        .select(`
          plan_id,
          status,
          current_period_end,
          subscription_plans!user_subscriptions_plan_id_fkey (
            monthly_credits
          )
        `)
        .eq('user_id', user.id)
        .in('status', ['active', 'canceled'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subError && subError.code !== 'PGRST116') {
        console.error('Error fetching subscription data:', subError);
        throw subError;
      }

      let monthlyCredits = 0;
      
      if (!subscriptionData || !subscriptionData.subscription_plans) {
        // No active subscription, but user might still have purchased credits
        console.log('No active subscription found, checking for purchased credits');
        monthlyCredits = 3; // Default trial credits
      } else {
        monthlyCredits = subscriptionData.subscription_plans?.monthly_credits || 0;
      }

      // For canceled subscriptions, check if they're still within the billing period
      if (subscriptionData && subscriptionData.status === 'canceled') {
        const now = new Date();
        const periodEnd = subscriptionData.current_period_end ? new Date(subscriptionData.current_period_end) : null;
        
        // If subscription is canceled and past the billing period, use trial credits only
        if (!periodEnd || now >= periodEnd) {
          console.log('Canceled subscription has expired, using trial credits');
          monthlyCredits = 3;
        } else {
          console.log(`Canceled subscription still active until ${periodEnd?.toISOString()}`);
        }
      }

      // Get usage data from subscription_limits table, including purchased credits
      const { data: limitData, error: limitError } = await supabase
        .from('subscription_limits')
        .select('current_analyses_used, purchased_analysis_credits')
        .eq('user_id', user.id)
        .maybeSingle();

      if (limitError) {
        console.error('Error fetching subscription limits:', limitError);
      }

      const usedCredits = limitData?.current_analyses_used || 0;
      const purchasedCredits = limitData?.purchased_analysis_credits || 0;
      
      // Calculate available plan credits: Monthly Credits - Used Credits
      const availablePlanCredits = Math.max(0, monthlyCredits - usedCredits);
      
      // Total credits = available plan credits + purchased credits
      const totalCredits = availablePlanCredits + purchasedCredits;

      return totalCredits;
    } catch (error) {
      console.error('Get credit balance error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getCreditBalance',
          component: 'CreditService',
        },
      });
      // Fallback to old system if subscription-based calculation fails
      try {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          console.error('Auth error in fallback:', authError);
          return 0;
        }

        const { data: userData, error: fallbackError } = await supabase
          .from('users')
          .select('credits')
          .eq('id', authUser.id)
          .single();

        if (fallbackError) throw fallbackError;
        return userData.credits || 0;
      } catch (fallbackError) {
        console.error('Fallback credit balance error:', fallbackError);
        Sentry.captureException(fallbackError, {
          tags: {
            operation: 'getCreditBalance-fallback',
            component: 'CreditService',
          },
        });
        return 0;
      }
    }
  }

  /**
   * Add credits to user account (adds to rollover credits in subscription system)
   */
  static async addCredits(
    amount: number,
    type: CreditTransaction['type'] = 'addition',
    reason: string,
    metadata?: CreditTransaction['metadata']
  ): Promise<number> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current balance
      const currentBalance = await this.getCreditBalance();

      // For purchased credits, update the user's credit display directly
      if (type === 'purchase' || type === 'addition') {
        // Update users.credits display field directly for purchased credits
        const { error: updateError } = await supabase
          .from('users')
          .update({ 
            credits: currentBalance + amount,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (updateError) {
          throw updateError;
        }
      }

      // Calculate new balance after addition
      const newBalance = currentBalance + amount;

      // Log transaction
      await this.logCreditTransaction(amount, type, reason, metadata);

      // Broadcast credit change event
      broadcastCreditChange(
        type === 'purchase' ? 'credit_added' : 'credit_added',
        user.id,
        amount,
        newBalance,
        {
          reason,
          ...metadata
        }
      );

      return newBalance;
    } catch (error) {
      console.error('Add credits error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'addCredits',
          component: 'CreditService',
        },
        extra: {
          amount,
          reason,
        },
      });
      throw error;
    }
  }

  /**
   * Deduct credits for analysis - uses plan credits first, then purchased credits
   */
  static async deductCreditsForAnalysis(
    analysisId?: string
  ): Promise<{
    success: boolean;
    newBalance: number;
    cost: number;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Calculate total cost
      const cost = this.calculateAnalysisCost();
      
      // Get current balance
      const currentBalance = await this.getCreditBalance();

      // Check if user has enough credits
      if (currentBalance < cost) {
        return {
          success: false,
          newBalance: currentBalance,
          cost
        };
      }

      // Get current subscription data and limits
      const { data: subscriptionData } = await supabase
        .from('user_subscriptions')
        .select(`
          subscription_plans!user_subscriptions_plan_id_fkey (
            monthly_credits
          )
        `)
        .eq('user_id', user.id)
        .in('status', ['active', 'canceled'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const monthlyCredits = subscriptionData?.subscription_plans?.monthly_credits || 3; // Default to trial

      const { data: limitData, error: limitError } = await supabase
        .from('subscription_limits')
        .select('current_analyses_used, purchased_analysis_credits')
        .eq('user_id', user.id)
        .single();

      if (limitError) {
        console.error('Error fetching subscription limits:', limitError);
      }

      const currentUsed = limitData?.current_analyses_used || 0;
      const purchasedCredits = limitData?.purchased_analysis_credits || 0;
      
      // Calculate available plan credits
      const availablePlanCredits = Math.max(0, monthlyCredits - currentUsed);
      
      // Determine how to consume credits: plan credits first, then purchased
      let newUsed = currentUsed;
      let newPurchasedCredits = purchasedCredits;
      
      if (availablePlanCredits >= cost) {
        // Use plan credits
        newUsed = currentUsed + cost;
        console.log(`Using plan credits: ${cost} credit(s) from plan allowance`);
      } else {
        // Use remaining plan credits + purchased credits
        const planCreditsToUse = availablePlanCredits;
        const purchasedCreditsToUse = cost - planCreditsToUse;
        
        newUsed = currentUsed + planCreditsToUse;
        newPurchasedCredits = purchasedCredits - purchasedCreditsToUse;
        
        console.log(`Using mixed credits: ${planCreditsToUse} from plan, ${purchasedCreditsToUse} from purchased`);
      }
      
      // Update subscription limits
      const { error: updateError } = await supabase
        .from('subscription_limits')
        .upsert({
          user_id: user.id,
          current_analyses_used: newUsed,
          purchased_analysis_credits: newPurchasedCredits,
          current_period_start: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });

      if (updateError) {
        throw updateError;
      }

      // Calculate new balance after deduction
      const newBalance = currentBalance - cost;

      // Log transaction
      const allPlatforms = ['Meta', 'Google Ads', 'YouTube', 'TikTok', 'Pinterest', 'Microsoft Ads'];
      await this.logCreditTransaction(
        -cost,
        'deduction',
        `Comprehensive analysis for all platforms: ${allPlatforms.join(', ')}`,
        { analysisId, platform: 'all_platforms' }
      );

      // Check for low credit warnings and send email notifications
      await this.checkAndSendLowCreditWarning(user.id, newBalance);

      // Broadcast credit consumption event
      broadcastCreditChange(
        'credit_consumed',
        user.id,
        -cost,
        newBalance,
        {
          analysisId,
          reason: `Comprehensive analysis for all platforms: ${allPlatforms.join(', ')}`
        }
      );

      // Update users.credits display field
      await this.updateUsersCreditDisplay(user.id);

      return {
        success: true,
        newBalance,
        cost
      };
    } catch (error) {
      console.error('Deduct credits error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'deductCredits',
          component: 'CreditService',
        },
        extra: {
          cost,
          analysisId,
        },
      });
      throw error;
    }
  }

  /**
   * Deduct credits for creative enhancement (1 credit per enhancement)
   */
  static async deductCreditsForEnhancement(
    originalCreativeId: string,
    enhancementId?: string
  ): Promise<{
    success: boolean;
    newBalance: number;
    cost: number;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Enhancement cost is always 1 credit
      const cost = 1;
      
      // Get current balance
      const currentBalance = await this.getCreditBalance();

      // Check if user has enough credits
      if (currentBalance < cost) {
        return {
          success: false,
          newBalance: currentBalance,
          cost
        };
      }

      // Get current subscription limits and plan info
      const { data: limitData, error: limitError } = await supabase
        .from('subscription_limits')
        .select('current_analyses_used')
        .eq('user_id', user.id)
        .single();

      if (limitError) {
        console.error('Error fetching subscription limits:', limitError);
      }

      const currentUsed = limitData?.current_analyses_used || 0;
      const newUsed = currentUsed + cost;
      
      // Update subscription limits with usage
      const { error: updateError } = await supabase
        .from('subscription_limits')
        .upsert({
          user_id: user.id,
          current_analyses_used: newUsed,
          current_period_start: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });

      if (updateError) {
        throw updateError;
      }

      // Calculate new balance after deduction (for display purposes)
      const newBalance = currentBalance - cost;

      // Log transaction
      await this.logCreditTransaction(
        -cost,
        'deduction',
        'AI Creative Enhancement',
        { 
          enhancementId, 
          originalCreativeId,
          platform: 'enhancement'
        }
      );

      // Check for low credit warnings and send email notifications
      await this.checkAndSendLowCreditWarning(user.id, newBalance);

      // Broadcast credit consumption event
      broadcastCreditChange(
        'credit_consumed',
        user.id,
        -cost,
        newBalance,
        {
          enhancementId,
          originalCreativeId,
          reason: 'AI Creative Enhancement'
        }
      );

      // Update users.credits display field
      await this.updateUsersCreditDisplay(user.id);

      return {
        success: true,
        newBalance,
        cost
      };
    } catch (error) {
      console.error('Deduct enhancement credits error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'deductEnhancementCredits',
          component: 'CreditService',
        },
        extra: {
          cost,
          reason,
        },
      });
      throw error;
    }
  }

  /**
   * Calculate analysis cost - now simplified to 1 credit per comprehensive analysis
   */
  static calculateAnalysisCost(): number {
    // All analyses now cost 1 credit regardless of platforms selected
    // Each analysis automatically checks ALL platforms
    return this.ANALYSIS_COST;
  }

  /**
   * Get credit usage statistics
   */
  static async getCreditUsage(monthsBack: number = 12): Promise<CreditUsage> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const currentBalance = await this.getCreditBalance();
      
      // Get transactions for the specified period
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - monthsBack);

      // This would need a credit_transactions table to implement properly
      // For now, return mock data structure
      const usage: CreditUsage = {
        totalUsed: 0,
        totalAvailable: currentBalance,
        currentMonth: {
          used: 0,
          available: currentBalance
        },
        breakdown: []
      };

      return usage;
    } catch (error) {
      console.error('Get credit usage error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getCreditUsage',
          component: 'CreditService',
        },
      });
      throw error;
    }
  }

  /**
   * Get available subscription plans
   */
  static async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const { data: plans, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price_monthly', { ascending: true });

      if (error) {
        console.error('Failed to fetch subscription plans:', error);
        return this.SUBSCRIPTION_PLANS; // Fallback to hardcoded
      }

      // Map database plans to SubscriptionPlan interface
      return plans.map(plan => ({
        id: plan.id,
        name: plan.name,
        monthlyCredits: plan.monthly_credits,
        priceMonthly: plan.price_monthly, // Already in cents
        priceYearly: 0, // Not using yearly pricing yet
        features: this.getPlanFeatures(plan.id),
        stripePriceIdMonthly: plan.stripe_price_id_monthly,
        isPopular: plan.id === 'pro'
      }));
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getSubscriptionPlans',
          component: 'CreditService',
        },
      });
      return this.SUBSCRIPTION_PLANS; // Fallback to hardcoded
    }
  }

  /**
   * Get plan features based on plan ID
   */
  private static getPlanFeatures(planId: string): string[] {
    const featureMap: Record<string, string[]> = {
      trial: [
        '3 comprehensive analyses per month',
        'All platform compliance checking',
        'Basic support',
        '1 project',
        '1 GB storage',
        'Credits reset monthly'
      ],
      starter: [
        '25 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'Priority support',
        '3 projects',
        '15 GB storage',
        '25% credit rollover'
      ],
      pro: [
        '100 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'Priority support',
        'Unlimited projects',
        'Team collaboration (up to 3 members)',
        '50 GB storage',
        'Detailed analytics',
        'Export reports'
      ],
      agency: [
        '350 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'Priority support',
        'Unlimited projects',
        'Team collaboration (up to 10 members)',
        '200 GB storage',
        'Advanced analytics',
        'Export reports',
        'Priority phone support'
      ],
      enterprise: [
        '1000 comprehensive analyses per month',
        'All platform compliance & performance analysis',
        'Dedicated support',
        'Unlimited projects',
        'Unlimited team collaboration',
        '1 TB storage',
        'Advanced analytics',
        'Export reports',
        'Dedicated account manager',
        'Custom integrations'
      ]
    };
    return featureMap[planId] || [];
  }

  /**
   * Get user's current subscription plan
   */
  static async getCurrentSubscriptionPlan(): Promise<SubscriptionPlan> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get subscription plan from user_subscriptions table (more reliable)
      const { data: subscriptionData, error: subError } = await supabase
        .from('user_subscriptions')
        .select(`
          plan_id,
          status,
          current_period_end,
          subscription_plans!user_subscriptions_plan_id_fkey (
            id,
            name,
            monthly_credits,
            price_monthly,
            team_seats_limit,
            projects_limit,
            versions_limit,
            stripe_price_id_monthly
          )
        `)
        .eq('user_id', user.id)
        .in('status', ['active', 'canceled'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subError && subError.code !== 'PGRST116') {
        console.error('Error fetching subscription plan:', subError);
        throw new Error(`Failed to fetch subscription plan: ${subError.message}`);
      }

      if (!subscriptionData || !subscriptionData.subscription_plans) {
        // Fallback to trial plan if no active subscription
        console.log('No active subscription found, using trial plan');
        const plans = await this.getSubscriptionPlans();
        return plans.find(plan => plan.id === 'trial') || plans[0];
      }

      // For canceled subscriptions, check if they're still within the billing period
      if (subscriptionData.status === 'canceled') {
        const now = new Date();
        const periodEnd = subscriptionData.current_period_end ? new Date(subscriptionData.current_period_end) : null;
        
        // If subscription is canceled and past the billing period, fallback to trial
        if (!periodEnd || now >= periodEnd) {
          console.log('Canceled subscription has expired, using trial plan');
          const plans = await this.getSubscriptionPlans();
          return plans.find(plan => plan.id === 'trial') || plans[0];
        }
        
        console.log(`Canceled subscription still active until ${periodEnd?.toISOString()}`);
      }

      const dbPlan = subscriptionData.subscription_plans;
      if (!dbPlan) {
        throw new Error('Invalid subscription plan data');
      }

      // Map database plan to SubscriptionPlan interface
      return {
        id: dbPlan.id,
        name: dbPlan.name,
        monthlyCredits: dbPlan.monthly_credits,
        priceMonthly: dbPlan.price_monthly, // Already in cents
        priceYearly: 0, // Not using yearly pricing yet
        features: this.getPlanFeatures(dbPlan.id),
        stripePriceIdMonthly: dbPlan.stripe_price_id_monthly,
        isPopular: dbPlan.id === 'pro'
      };
    } catch (error) {
      console.error('Get current subscription error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getCurrentSubscription',
          component: 'CreditService',
        },
      });
      // Fallback to trial plan on error
      const plans = await this.getSubscriptionPlans();
      return plans.find(plan => plan.id === 'trial') || plans[0];
    }
  }

  /**
   * Update user's subscription plan
   */
  static async updateSubscriptionPlan(
    planId: string,
    stripeSubscriptionId?: string
  ): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const plan = this.SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Invalid subscription plan');
      }

      // Update user's subscription plan
      const { error: updateError } = await supabase
        .from('users')
        .update({ subscription_plan: planId })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Add monthly credits for the new plan
      if (plan.monthlyCredits > 0) {
        await this.addCredits(
          plan.monthlyCredits,
          'subscription_renewal',
          `Monthly credits for ${plan.name} plan`,
          { subscriptionId: stripeSubscriptionId }
        );
      }
    } catch (error) {
      console.error('Update subscription plan error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'updateSubscriptionPlan',
          component: 'CreditService',
        },
        extra: {
          newPlan,
        },
      });
      throw error;
    }
  }

  /**
   * Check if user can perform analysis (simplified to 1 credit per analysis)
   */
  static async canPerformAnalysis(): Promise<{
    canAnalyze: boolean;
    cost: number;
    currentBalance: number;
    message?: string;
  }> {
    try {
      const cost = this.calculateAnalysisCost(); // Always 1 credit
      const currentBalance = await this.getCreditBalance();

      if (currentBalance >= cost) {
        return {
          canAnalyze: true,
          cost,
          currentBalance
        };
      }

      return {
        canAnalyze: false,
        cost,
        currentBalance,
        message: `Insufficient credits. You need ${cost} credit but only have ${currentBalance}.`
      };
    } catch (error) {
      console.error('Check analysis capability error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'checkAnalysisCapability',
          component: 'CreditService',
        },
        extra: {
          cost,
        },
      });
      throw error;
    }
  }

  /**
   * Get credit cost preview - simplified to 1 credit per analysis
   */
  static getCostPreview(): {
    total: number;
    breakdown: Array<{ platform: string; cost: number; }>;
  } {
    // All platforms are analyzed automatically for 1 credit
    const allPlatforms = ['Meta', 'Google Ads', 'YouTube', 'TikTok', 'Pinterest', 'Microsoft Ads'];
    const breakdown = allPlatforms.map(platform => ({
      platform,
      cost: 0 // Individual platform cost is 0 since it's all bundled for 1 credit
    }));

    return { 
      total: this.ANALYSIS_COST, 
      breakdown 
    };
  }

  /**
   * Log credit transaction using CreditAnalyticsService
   */
  private static async logCreditTransaction(
    amount: number,
    type: CreditTransaction['type'],
    reason: string,
    metadata?: CreditTransaction['metadata']
  ): Promise<void> {
    try {
      // Use CreditAnalyticsService to record the transaction
      await CreditAnalyticsService.recordCreditTransaction(
        amount,
        type,
        reason,
        metadata || {},
        metadata?.analysisId,
        metadata?.subscriptionId,
        metadata?.paymentIntentId
      );
    } catch (error) {
      console.warn('Failed to log credit transaction:', error);
      // Don't throw - this is just logging
    }
  }

  /**
   * Refund credits (for failed analyses)
   */
  static async refundCredits(
    amount: number,
    reason: string,
    metadata?: CreditTransaction['metadata']
  ): Promise<number> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const newBalance = await this.addCredits(amount, 'addition', `Refund: ${reason}`, metadata);
    
    // Broadcast refund event
    broadcastCreditChange(
      'credit_refunded',
      user.id,
      amount,
      newBalance,
      {
        reason: `Refund: ${reason}`,
        ...metadata
      }
    );

    return newBalance;
  }

  /**
   * Get analysis cost information - simplified to 1 credit for all platforms
   */
  static getAnalysisCosts(): Record<string, number> {
    // Return all platforms with 0 individual cost since it's bundled
    return {
      'Meta (Facebook/Instagram)': 0,
      'Google Ads': 0,
      'YouTube': 0,
      'TikTok': 0,
      'Pinterest': 0,
      'Microsoft Ads': 0,
      'Comprehensive Analysis': this.ANALYSIS_COST
    };
  }

  /**
   * Check if user needs to upgrade subscription
   */
  static async getUpgradeRecommendation(): Promise<{
    shouldUpgrade: boolean;
    currentPlan: SubscriptionPlan;
    recommendedPlan?: SubscriptionPlan;
    reason?: string;
  }> {
    try {
      const currentPlan = await this.getCurrentSubscriptionPlan();
      const currentBalance = await this.getCreditBalance();

      // Simple logic: recommend upgrade if credits are low and user is on trial plan
      if (currentPlan.id === 'trial' && currentBalance < 3) {
        return {
          shouldUpgrade: true,
          currentPlan,
          recommendedPlan: this.SUBSCRIPTION_PLANS.find(p => p.id === 'professional'),
          reason: 'Your credits are running low. Upgrade to get more monthly credits.'
        };
      }

      return {
        shouldUpgrade: false,
        currentPlan
      };
    } catch (error) {
      console.error('Get upgrade recommendation error:', error);
      throw error;
    }
  }

  /**
   * Get detailed credit information for the banner/dashboard
   */
  static async getDetailedCreditInfo(): Promise<{
    totalCredits: number;
    usedThisMonth: number;
    rolloverCredits: number;
    planName: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current credit balance
      const totalCredits = await this.getCreditBalance();

      // Get usage data from subscription_limits table - single source of truth
      const { data: limitData, error: limitError } = await supabase
        .from('subscription_limits')
        .select('current_analyses_used, purchased_analysis_credits')
        .eq('user_id', user.id)
        .maybeSingle();

      if (limitError) {
        console.error('Error fetching usage data from subscription_limits:', limitError);
      }

      // Use subscription_limits as the single source of truth for usage tracking
      const usedThisMonth = limitData?.current_analyses_used || 0;

      // Get current plan name
      const currentPlan = await this.getCurrentSubscriptionPlan();
      const planName = currentPlan.name;

      return {
        totalCredits,
        usedThisMonth,
        planName
      };
    } catch (error) {
      console.error('Get detailed credit info error:', error);
      // Return defaults on error
      return {
        totalCredits: 0,
        usedThisMonth: 0,
        planName: 'Free Trial'
      };
    }
  }

  /**
   * Check and send low credit warning emails
   */
  private static async checkAndSendLowCreditWarning(userId: string, newBalance: number): Promise<void> {
    try {
      // Get user's plan details to calculate percentage
      const planDetails = await this.getCurrentSubscriptionPlan();
      const monthlyAllowance = planDetails.monthlyCredits;
      
      // Calculate percentage of credits remaining
      const percentageRemaining = (newBalance / monthlyAllowance) * 100;
      
      // Get user details for email
      const { data: userData } = await supabase
        .from('users')
        .select('name, email')
        .eq('id', userId)
        .single();
      
      if (!userData || !userData.email) {
        console.log('No user data or email found for low credit warning');
        return;
      }
      
      // Check if we should send a warning (20% threshold)
      if (percentageRemaining <= 20 && percentageRemaining > 0) {
        // Check if we've already sent a warning this billing period
        const { data: lastWarning } = await supabase
          .from('email_logs')
          .select('created_at')
          .eq('user_id', userId)
          .eq('template', 'credits-running-low')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        // Only send if we haven't sent a warning in the last 7 days
        const lastWarningDate = lastWarning?.created_at ? new Date(lastWarning.created_at) : null;
        const daysSinceLastWarning = lastWarningDate 
          ? (Date.now() - lastWarningDate.getTime()) / (1000 * 60 * 60 * 24)
          : Infinity;
        
        if (daysSinceLastWarning > 7) {
          console.log('📧 Sending low credit warning email...');
          
          await EmailService.send({
            to: userData.email,
            template: 'credits-running-low',
            data: {
              userName: userData.name || 'there',
              creditsRemaining: newBalance,
              percentageRemaining: Math.round(percentageRemaining),
              planName: planDetails.name,
              upgradeUrl: `${import.meta.env.VITE_APP_URL || 'http://localhost:5173'}/credits`
            },
            userId: userId
          });
          
          console.log('✅ Low credit warning email sent successfully');
        }
      }
      
      // Check if credits are completely exhausted
      if (newBalance === 0) {
        // Check if we've already sent an out-of-credits email this billing period
        const { data: lastOutOfCredits } = await supabase
          .from('email_logs')
          .select('created_at')
          .eq('user_id', userId)
          .eq('template', 'credits-exhausted')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        const lastExhaustedDate = lastOutOfCredits?.created_at ? new Date(lastOutOfCredits.created_at) : null;
        const daysSinceLastExhausted = lastExhaustedDate 
          ? (Date.now() - lastExhaustedDate.getTime()) / (1000 * 60 * 60 * 24)
          : Infinity;
        
        if (daysSinceLastExhausted > 30) {
          console.log('📧 Sending credits exhausted email...');
          
          await EmailService.send({
            to: userData.email,
            template: 'credits-exhausted',
            data: {
              userName: userData.name || 'there',
              planName: planDetails.name,
              upgradeUrl: `${import.meta.env.VITE_APP_URL || 'http://localhost:5173'}/credits`
            },
            userId: userId
          });
          
          console.log('✅ Credits exhausted email sent successfully');
        }
      }
    } catch (error) {
      console.error('❌ Error checking/sending low credit warning:', error);
      // Don't throw - this shouldn't break the credit deduction flow
    }
  }
}

export default CreditService; 